import styled from '@emotion/styled'
import viewport from '@mapbox/geo-viewport'
import StaticMapService from '@mapbox/mapbox-sdk/services/static'
import { useMemo, useRef } from 'react'
import { StaticMapDisplay } from 'containers/event/map/StaticMapDisplay'
import { NEXT_PUBLIC_MAPBOX_TOKEN } from 'containers/event/map/map-env'
import { isNotNullOrUndefined } from 'helpers/array'

export const calculateMapBounds = (
  features: GeoJSON.Feature<GeoJSON.Point>[] | undefined
): [number, number, number, number] | null => {
  if (!features?.length) {
    return null
  }
  const coordinates = features.map((f) => f.geometry.coordinates)
  const longitudes = coordinates
    .map((coord) => coord[0])
    .filter(isNotNullOrUndefined)
  const latitudes = coordinates
    .map((coord) => coord[1])
    .filter(isNotNullOrUndefined)

  const minLng = Math.min(...longitudes)
  const maxLng = Math.max(...longitudes)
  const minLat = Math.min(...latitudes)
  const maxLat = Math.max(...latitudes)

  return [minLng, minLat, maxLng, maxLat]
}

export const getCenterZoomFromBounds = (
  bounds: [number, number, number, number],
  imageWidth: number,
  imageHeight: number,
  padding = 50
): { center: [number, number]; zoom: number } => {
  const dimensions: [number, number] = [
    imageWidth - padding * 2,
    imageHeight - padding * 2
  ]
  const { center, zoom } = viewport.viewport(
    bounds,
    dimensions,
    0,
    22,
    512,
    true
  )
  return { center, zoom: Math.min(zoom, 13) }
}

interface Props {
  geoJson?: GeoJSON.FeatureCollection<GeoJSON.Point>
  pointerEventsNone: boolean
  onMouseDown: () => void
}

const staticMapService = StaticMapService({
  accessToken: NEXT_PUBLIC_MAPBOX_TOKEN
})

export const SearchStaticMap = ({
  onMouseDown,
  pointerEventsNone,
  geoJson
}: Props) => {
  const mapRef = useRef<HTMLDivElement>(null)

  const staticMapUrl = useMemo(() => {
    // default arbitrary bounds
    const bounds = calculateMapBounds(geoJson?.features) ?? [
      -3.954726, 45.3870055, 6.2740665, 51.015681
    ]
    if (!mapRef.current) {
      return ''
    }
    const rect = mapRef.current?.getBoundingClientRect()
    const imageWidth = rect.width
    const imageHeight = rect.height
    const { center, zoom } = getCenterZoomFromBounds(
      bounds,
      imageWidth,
      imageHeight,
      50
    )
    const staticImageRequest = staticMapService.getStaticImage({
      position: { coordinates: center, zoom },
      overlays: geoJson?.features.map((feature) => ({
        marker: {
          url: 'https://peyce.s3.eu-west-3.amazonaws.com/selected-3.png',
          coordinates: [
            feature.geometry.coordinates[0]!,
            feature.geometry.coordinates[1]!
          ]
        }
      })),
      ownerId: 'peyce',
      styleId: 'cl30azfve005k14qnod0isbro',
      height: Math.round(Math.min(1280, imageHeight)),
      width: Math.round(imageWidth),
      highRes: true
    })
    return staticImageRequest.url()
  }, [geoJson])

  return (
    <StaticMapDiv pointerEventsNone={pointerEventsNone} ref={mapRef}>
      <StaticMapDisplay onMouseDown={onMouseDown} staticMapUrl={staticMapUrl} />
    </StaticMapDiv>
  )
}

interface MapContainerDivProps {
  pointerEventsNone: boolean
}

export const StaticMapDiv = styled.div<MapContainerDivProps>`
  pointer-events: ${(props) => (props.pointerEventsNone ? 'none' : 'auto')};
  width: 100%;
  height: 100%;
`
